import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { PI_PHASES, PI_PHASE_ORDER, getPiWorkspacePath } from './projectIntelligencePhases';

export default function ProjectIntelligenceWorkspaceShell({
  phase,
  projectId,
  projectTitle,
  loading = false,
  notFound = false,
  actions = [],
  children,
}) {
  const navigate = useNavigate();
  const phaseConfig = PI_PHASES[phase];
  const currentIndex = PI_PHASE_ORDER.indexOf(phase);

  const breadcrumbPaths = [
    { label: 'Sourcing', url: '/sourcing' },
    { label: 'Project Intelligence', url: '/sourcing/project-intelligence' },
    { label: phaseConfig.label, url: phaseConfig.listPath },
  ];
  if (projectTitle) {
    breadcrumbPaths.push({ label: projectTitle });
  }

  if (loading) {
    return (
      <>
        <PageBreadcrumb paths={breadcrumbPaths} />
        <div className="page-container d-flex align-items-center justify-content-center py-5">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p className="text-muted mb-0">Loading project workspace...</p>
          </div>
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageBreadcrumb paths={breadcrumbPaths} />
        <div className="page-container text-center py-5">
          <h4 className="fw-bold">Project not found</h4>
          <p className="text-muted">The project may have been removed or the link is invalid.</p>
          <button
            type="button"
            className="btn btn-primary mt-3"
            onClick={() => navigate(phaseConfig.listPath)}
          >
            Back to {phaseConfig.label}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb paths={breadcrumbPaths} />
      <PageHeader
        title={phaseConfig.label}
        description={
          projectTitle
            ? `${projectTitle} — ${phaseConfig.description}`
            : phaseConfig.description
        }
        actions={actions}
      />

      <div className="page-container pt-0">
        <div className="pi-workspace-inner">
          <nav className="pi-phase-stepper mb-4" aria-label="Project Intelligence phases">
            {PI_PHASE_ORDER.map((stepKey, index) => {
              const step = PI_PHASES[stepKey];
              const isComplete = index < currentIndex;
              const isCurrent = stepKey === phase;
              const isClickable = Boolean(projectId);
              const stepClass = [
                'pi-phase-step',
                isCurrent ? 'pi-phase-step--active' : '',
                isComplete ? 'pi-phase-step--complete' : '',
                isClickable ? 'pi-phase-step--clickable' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <React.Fragment key={stepKey}>
                  <button
                    type="button"
                    className={stepClass}
                    disabled={!isClickable}
                    onClick={() => isClickable && navigate(getPiWorkspacePath(stepKey, projectId))}
                  >
                    <span className="pi-phase-step__index">
                      {isComplete ? <Check size={14} /> : index + 1}
                    </span>
                    <span className="pi-phase-step__label">{step.label}</span>
                  </button>
                  {index < PI_PHASE_ORDER.length - 1 && (
                    <ChevronRight size={16} className="pi-phase-stepper__chevron text-muted" />
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          {children}
        </div>
      </div>
    </>
  );
}
